import { useState, FormEvent } from "react";
import { applyActionCode } from "firebase/auth";
import { auth } from "../../firebaseApp.ts";
import LoadingFrame from "../../components/LoadingFrame.tsx";
import { Link } from "react-router-dom";
import { Logo } from "../../components/specific/Logo.tsx";

interface Props {
    actionCode: string;
    continueUrl: string;
}

export function VerifyEmailActionHandler({ actionCode, continueUrl }: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [success, setSuccess] = useState(false);

    async function handleEmailVerification(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (!actionCode) {
            setHasError(true);
            return;
        }

        const formData = new FormData(e.currentTarget);
        const is_bot = formData.get('is_bot') === 'true';

        if (is_bot) {
            setHasError(true);
            return;
        }

        setIsLoading(true);
        setHasError(false);

        try {
            await applyActionCode(auth, actionCode);
            setSuccess(true);
        } catch (e: any) {
            setHasError(true);
            throw e;
        } finally {
            setIsLoading(false);
        }
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center space-y-6">
                    <div className="flex justify-center">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center border-2 border-green-200">
                            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h1 className="text-4xl font-bold text-gray-900">
                            Gefeliciteerd!
                        </h1>
                        <h2 className="text-2xl font-semibold text-gray-800">
                            E-mailadres geverifieerd
                        </h2>
                        <p className="text-lg text-gray-600">
                            Je account is succesvol geverifieerd. Welkom bij PocketCFO!
                        </p>
                    </div>

                    {continueUrl && (
                        <button
                            onClick={() => window.location.href = continueUrl}
                            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                            Naar inlogpagina
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                <LoadingFrame isLoading={isLoading}>
                    <div className="text-center space-y-6">
                        <div className="mb-8 flex justify-center">
                            <Logo />
                        </div>
                        {hasError ? (
                            <div className="space-y-4">
                                <h1 className="text-3xl font-bold text-gray-900">Ongeldige verificatielink</h1>
                                <p className="text-gray-600">
                                    Oeps, deze code is ongeldig of al gebruikt. Ga naar de inlogpagina en probeer in te loggen.
                                </p>
                                <Link
                                    to="/"
                                    className="inline-flex items-center justify-center w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
                                >
                                    Naar inlogpagina
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4">
                                    <h1 className="text-3xl font-bold text-gray-900">Verifieer je e-mailadres</h1>
                                    <p className="text-gray-600">
                                        Klik op de knop hieronder om je account te bevestigen en te beginnen.
                                    </p>
                                </div>
                                
                                <form className="space-y-6" onSubmit={handleEmailVerification}>
                                    <input
                                        type="checkbox"
                                        name="is_bot"
                                        value="true"
                                        className="absolute opacity-0 pointer-events-none"
                                        style={{ position: 'absolute', left: '-9999px' }}
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!actionCode || isLoading}
                                        className="w-full flex justify-center items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>E-mailadres verifiëren</span>
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </LoadingFrame>
            </div>
        </div>
    );
}
